import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronRight, Crown, Users, AlertCircle, Eye, Heart, TrendingUp, TrendingDown, Link2 } from "lucide-react";
import { toast } from "sonner";
import EventFeed from "@/components/dashboard/EventFeed";
import KpiCard from "@/components/dashboard/KpiCard";
import {
    getGroup,
    updateGroupSettings,
    groupEvents,
    groupStats,
    channelGrowth,
    channelJoinsLeaves,
    channelInviteSources,
} from "@/lib/api";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Legend, ComposedChart,
} from "recharts";

const TT = { contentStyle: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 } };
const TK = { fill: "hsl(var(--muted-foreground))", fontSize: 10 };

// ---- Toggles for channels vs groups ----
const CHANNEL_TOGGLES = [
    { key: "anti_fakeboost_enabled", label: "🕵️ Anti-FakeBoost (انضمامات وهمية)", hint: "كشف الأعضاء الوهميين" },
    { key: "anti_fakeviews_enabled", label: "👁 Anti-FakeViews (مشاهدات وهمية)", hint: "كشف تضخيم المشاهدات" },
    { key: "anti_fakereactions_enabled", label: "❤️ Anti-FakeReactions (تفاعلات وهمية)", hint: "كشف تضخيم التفاعلات" },
    { key: "fakeboost_premium_immune", label: "💎 Premium محصّن", hint: "حسابات Premium لا تُلمس" },
    { key: "anti_raid_enabled", label: "🚨 Anti-Raid", hint: "رصد موجات الانضمام" },
    { key: "staged_protection_enabled", label: "🛡 حماية متدرّجة", hint: "تنبيهات تتدرّج في الخطورة" },
];

const GROUP_TOGGLES = [
    { key: "anti_fakeboost_enabled", label: "🕵️ Anti-FakeBoost (انضمامات وهمية)", hint: "كشف الأعضاء الوهميين" },
    { key: "fakeboost_premium_immune", label: "💎 Premium محصّن", hint: "حسابات Premium لا تُلمس" },
    { key: "anti_raid_enabled", label: "🚨 Anti-Raid", hint: "رصد موجات الانضمام" },
    { key: "staged_protection_enabled", label: "🛡 حماية متدرّجة", hint: "تنبيهات تتدرّج في الخطورة" },
    { key: "silent_mode", label: "وضع الصمت", hint: "تنبيهات فقط بدون إجراء" },
];

const FB_ACTIONS = [
    { value: "alert_only", label: "تنبيه فقط (الأكثر أماناً)" },
    { value: "mute_new", label: "كتم صامت 24س" },
    { value: "kick_only", label: "طرد صامت" },
    { value: "ban_strict", label: "حظر نهائي ⚠️" },
];
const FV_ACTIONS = [
    { value: "alert_only", label: "تنبيه DM فقط" },
    { value: "alert_and_log", label: "تنبيه + سجل تفصيلي" },
];
const FR_ACTIONS = [
    { value: "alert_only", label: "تنبيه DM فقط" },
    { value: "alert_and_log", label: "تنبيه + سجل تفصيلي" },
];
const STRICTNESS_LEVELS = [
    { value: "relaxed", label: "🟢 مرن" },
    { value: "balanced", label: "🟡 متوازن" },
    { value: "strict", label: "🔴 صارم" },
];

export default function GroupDetail() {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isChannel = location.pathname.startsWith("/channels");
    const [group, setGroup] = useState(null);
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState(null);
    const [growth, setGrowth] = useState([]);
    const [joinsLeaves, setJoinsLeaves] = useState([]);
    const [inviteSources, setInviteSources] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [g, e, s] = await Promise.all([
                getGroup(chatId),
                groupEvents(chatId, 50),
                groupStats(chatId),
            ]);
            setGroup(g);
            setEvents(e.events || []);
            setStats(s);
            // Load channel-specific data
            if (g.chat_type === "channel" || isChannel) {
                try {
                    const [gr, jl, is] = await Promise.all([
                        channelGrowth(chatId, 7),
                        channelJoinsLeaves(chatId, 7),
                        channelInviteSources(chatId),
                    ]);
                    setGrowth(gr.series || []);
                    setJoinsLeaves(jl.series || []);
                    setInviteSources(is.sources || []);
                } catch {}
            }
        } catch {
            toast.error("فشل تحميل البيانات");
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, [chatId]);

    const onToggle = async (key, value) => {
        try {
            const updated = await updateGroupSettings(chatId, { [key]: value });
            setGroup({ ...group, settings: updated.settings });
            toast.success(`تم ${value ? "تفعيل" : "إيقاف"} الخيار`);
        } catch { toast.error("فشل التحديث"); }
    };

    const onSelectChange = async (key, value) => {
        try {
            const updated = await updateGroupSettings(chatId, { [key]: value });
            setGroup({ ...group, settings: updated.settings });
            toast.success("تم التحديث");
        } catch { toast.error("فشل التحديث"); }
    };

    if (loading) return <Skeleton className="h-96 w-full" />;
    if (!group) return null;

    const settings = group.settings || {};
    const toggles = (group.chat_type === "channel" || isChannel) ? CHANNEL_TOGGLES : GROUP_TOGGLES;
    const backPath = isChannel ? "/channels" : "/groups";
    const backLabel = isChannel ? "القنوات" : "المجموعات";

    return (
        <div className="space-y-4 md:space-y-6">
            <button
                onClick={() => navigate(backPath)}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="group-detail-back-button"
            >
                {backLabel}
                <ChevronRight className="h-4 w-4" />
            </button>

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate">{group.title}</h1>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-muted-foreground" dir="ltr">{group.chat_id}</span>
                        {group.username && <Badge variant="outline" className="text-[10px]" dir="ltr">@{group.username}</Badge>}
                        <Badge variant="outline">{group.chat_type === "channel" ? "قناة" : group.chat_type}</Badge>
                        <Badge variant="outline" className="text-emerald-300 bg-emerald-500/10 border-emerald-500/20" data-testid="group-detail-health-badge">
                            صحة {group.health_score}%
                        </Badge>
                    </div>
                </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <KpiCard title="الأعضاء" value={stats?.members ?? group.member_count ?? 0} icon={Users} tone="info" testId="group-kpi-members" />
                <KpiCard title="Premium" value={stats?.premium_members ?? 0} icon={Crown} tone="warning" testId="group-kpi-premium" />
                <KpiCard title="حالات حظر" value={stats?.bans ?? 0} icon={AlertCircle} tone="critical" testId="group-kpi-bans" />
                <KpiCard title="هجمات" value={stats?.raids ?? 0} icon={AlertCircle} tone="critical" testId="group-kpi-raids" />
            </div>

            <Tabs defaultValue="settings" data-testid="group-detail-tabs">
                <TabsList className="flex-wrap">
                    <TabsTrigger value="settings" data-testid="group-detail-tab-settings">الإعدادات</TabsTrigger>
                    {(group.chat_type === "channel" || isChannel) && <TabsTrigger value="charts" data-testid="group-detail-tab-charts">الرسوم البيانية</TabsTrigger>}
                    {(group.chat_type === "channel" || isChannel) && <TabsTrigger value="invite" data-testid="group-detail-tab-invite">روابط الدعوة</TabsTrigger>}
                    <TabsTrigger value="logs" data-testid="group-detail-tab-logs">السجلات</TabsTrigger>
                </TabsList>

                {/* Settings Tab */}
                <TabsContent value="settings">
                    <div className="space-y-4">
                        <Card className="bg-card/60 backdrop-blur" data-testid="group-detail-settings-panel">
                            <CardHeader><CardTitle>إعدادات الحماية</CardTitle></CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {toggles.map((t) => (
                                        <div key={t.key} className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-card/30 p-3">
                                            <div className="min-w-0">
                                                <Label htmlFor={t.key} className="cursor-pointer text-sm font-medium">{t.label}</Label>
                                                <p className="mt-0.5 text-xs text-muted-foreground">{t.hint}</p>
                                            </div>
                                            <Switch id={t.key} checked={Boolean(settings[t.key])} onCheckedChange={(v) => onToggle(t.key, v)} data-testid={`toggle-${t.key}`} />
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Action Selectors */}
                        <Card className="bg-card/60 backdrop-blur">
                            <CardHeader><CardTitle>إجراءات الكشف</CardTitle></CardHeader>
                            <CardContent className="space-y-4">
                                {/* FakeBoost Action */}
                                <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-card/30 p-3">
                                    <div><Label className="text-sm font-medium">🎯 إجراء FakeBoost</Label><p className="text-xs text-muted-foreground">ماذا يفعل البوت عند كشف انضمام وهمي</p></div>
                                    <Select value={settings.fakeboost_action || "alert_only"} onValueChange={(v) => onSelectChange("fakeboost_action", v)}>
                                        <SelectTrigger className="w-[200px]" data-testid="select-fakeboost-action"><SelectValue /></SelectTrigger>
                                        <SelectContent>{FB_ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                {/* Strictness */}
                                <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-card/30 p-3">
                                    <div><Label className="text-sm font-medium">⚙️ مستوى الصرامة</Label><p className="text-xs text-muted-foreground">حساسية الكشف</p></div>
                                    <Select value={settings.fakeboost_strictness || "balanced"} onValueChange={(v) => onSelectChange("fakeboost_strictness", v)}>
                                        <SelectTrigger className="w-[200px]" data-testid="select-strictness"><SelectValue /></SelectTrigger>
                                        <SelectContent>{STRICTNESS_LEVELS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                {/* FakeViews Action (channels) */}
                                {(group.chat_type === "channel" || isChannel) && (
                                    <>
                                        <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-card/30 p-3">
                                            <div><Label className="text-sm font-medium">👁 إجراء FakeViews</Label><p className="text-xs text-muted-foreground">عند كشف مشاهدات وهمية</p></div>
                                            <Select value={settings.fakeviews_action || "alert_only"} onValueChange={(v) => onSelectChange("fakeviews_action", v)}>
                                                <SelectTrigger className="w-[200px]" data-testid="select-fakeviews-action"><SelectValue /></SelectTrigger>
                                                <SelectContent>{FV_ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-card/30 p-3">
                                            <div><Label className="text-sm font-medium">❤️ إجراء FakeReactions</Label><p className="text-xs text-muted-foreground">عند كشف تفاعلات وهمية</p></div>
                                            <Select value={settings.fakereactions_action || "alert_only"} onValueChange={(v) => onSelectChange("fakereactions_action", v)}>
                                                <SelectTrigger className="w-[200px]" data-testid="select-fakereactions-action"><SelectValue /></SelectTrigger>
                                                <SelectContent>{FR_ACTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Charts Tab (channels) */}
                {(group.chat_type === "channel" || isChannel) && (
                    <TabsContent value="charts">
                        <div className="space-y-4">
                            {/* Member Growth */}
                            <Card className="bg-card/60 backdrop-blur">
                                <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-4 w-4" /> نمو الأعضاء</CardTitle></CardHeader>
                                <CardContent>
                                    {growth.length === 0 ? (
                                        <p className="text-sm text-muted-foreground text-center py-8">لا توجد بيانات نمو بعد. يتم جمعها كل دقيقة.</p>
                                    ) : (
                                        <div className="h-[280px]" dir="ltr">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={growth}>
                                                    <CartesianGrid strokeOpacity={0.1} vertical={false} />
                                                    <XAxis dataKey="ts" tick={TK} tickFormatter={(v) => v ? v.substring(11, 16) : ""} />
                                                    <YAxis tick={TK} domain={["dataMin - 10", "dataMax + 10"]} />
                                                    <Tooltip {...TT} />
                                                    <Line type="monotone" dataKey="count" stroke="hsl(188 92% 45%)" strokeWidth={2} dot={false} name="الأعضاء" />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Joins vs Leaves */}
                            <Card className="bg-card/60 backdrop-blur">
                                <CardHeader><CardTitle className="flex items-center gap-2">📊 الانضمام والمغادرة</CardTitle></CardHeader>
                                <CardContent>
                                    {joinsLeaves.length === 0 ? (
                                        <p className="text-sm text-muted-foreground text-center py-8">لا توجد بيانات بعد.</p>
                                    ) : (
                                        <div className="h-[280px]" dir="ltr">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <ComposedChart data={joinsLeaves}>
                                                    <CartesianGrid strokeOpacity={0.1} vertical={false} />
                                                    <XAxis dataKey="day" tick={TK} />
                                                    <YAxis tick={TK} />
                                                    <Tooltip {...TT} />
                                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                                    <Bar dataKey="joins" fill="hsl(158 64% 45%)" radius={[3,3,0,0]} name="انضموا" />
                                                    <Bar dataKey="leaves" fill="hsl(0 72% 52%)" radius={[3,3,0,0]} name="غادروا" />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                )}

                {/* Invite Links Tab (channels) */}
                {(group.chat_type === "channel" || isChannel) && (
                    <TabsContent value="invite">
                        <Card className="bg-card/60 backdrop-blur">
                            <CardHeader><CardTitle className="flex items-center gap-2"><Link2 className="h-4 w-4" /> مصادر الانضمام</CardTitle></CardHeader>
                            <CardContent>
                                {inviteSources.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-8">لا توجد بيانات روابط دعوة بعد.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {inviteSources.map((src, i) => (
                                            <div key={i} className="flex items-center justify-between rounded-lg border border-border/60 bg-card/30 p-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium truncate">{src.name || src.link.split("/").pop()}</p>
                                                    <p className="text-[11px] text-muted-foreground font-mono truncate" dir="ltr">{src.link}</p>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs shrink-0">
                                                    <span className="text-emerald-400">+{src.joins} انضمام</span>
                                                    <span className="text-rose-400">-{src.leaves} مغادرة</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

                {/* Logs Tab */}
                <TabsContent value="logs">
                    <Card className="bg-card/60 backdrop-blur">
                        <CardHeader><CardTitle>سجل الأحداث</CardTitle></CardHeader>
                        <CardContent><EventFeed events={events} height="h-[460px]" /></CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
